import React, { useEffect, useRef, useState, useCallback } from "react";
import SIPClient from "./SIPClient";
import { useAudioVisualizer } from "./hooks/useAudioVisualizer";
import { useRingtone } from "./hooks/useRingtone";
import { useRingback } from "./hooks/useRingback";
import "./SipPhoneWidget.css";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";

import { __updateSipSetting, __getSipSetting } from "../../../api/auth";
import useAxios from "../../../hooks/useAxios";

function SipPhoneWidget({
    sipConfig,
    callerIds = [],
    style = {}
}) {
    const config = sipConfig;
    const callerIdOptions = callerIds;

    const [isExpanded, setIsExpanded] = useState(false);
    const [isRegistered, setIsRegistered] = useState(false);

    const renderAudioSettings = () => (
        <div className="settings-popover" style={{ bottom: inCall ? '90px' : '20px', zIndex: 1000 }}>
            <div className="settings-group">
                <label className="settings-label">Microphone</label>
                <select
                    className="settings-select"
                    value={selectedInput}
                    onChange={handleMicChange}
                    onClick={(e) => e.stopPropagation()}
                >
                    {inputDevices.map(d => (
                        <option key={d.deviceId} value={d.deviceId}>{d.label || `Mic ${d.deviceId.slice(0, 5)}...`}</option>
                    ))}
                </select>
            </div>
            <div className="settings-group">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                    <label className="settings-label" style={{ marginBottom: 0 }}>Speaker</label>
                    <button
                        onClick={(e) => { e.stopPropagation(); handleTestSpeaker(); }}
                        style={{
                            backgroundColor: isPlayingTest ? '#ef4444' : '#6366f1',
                            border: 'none',
                            borderRadius: '4px',
                            color: 'white',
                            fontSize: '10px',
                            padding: '2px 8px',
                            cursor: 'pointer'
                        }}
                    >
                        {isPlayingTest ? "Stop" : "Test"}
                    </button>
                </div>
                <select
                    className="settings-select"
                    value={selectedOutput}
                    onChange={handleSpeakerChange}
                    onClick={(e) => e.stopPropagation()}
                >
                    {outputDevices.map(d => (
                        <option key={d.deviceId} value={d.deviceId}>{d.label || `Speaker ${d.deviceId.slice(0, 5)}...`}</option>
                    ))}
                </select>
            </div>

            {/* Audio Meter for Testing */}
            <div className="audio-meter" style={{ marginTop: '12px', padding: '10px' }}>
                <div className="meter-label">
                    <span>Mic Level</span>
                </div>
                <div className="meter-track">
                    <div className="meter-fill" style={{ width: `${Math.min(micLevel * 200, 100)}%` }}></div>
                </div>
            </div>
        </div>
    );
    const [isRegistering, setIsRegistering] = useState(false);
    const [inCall, setInCall] = useState(false);
    const [isCalling, setIsCalling] = useState(false);
    const [dialNumber, setDialNumber] = useState("");
    const [callStatus, setCallStatus] = useState("");
    const [callDuration, setCallDuration] = useState(0);
    const callTimerRef = useRef(null);
    const [sessionId, setSessionId] = useState("");
    const [isMuted, setIsMuted] = useState(false);
    const [outputDevices, setOutputDevices] = useState([]);
    const [selectedOutput, setSelectedOutput] = useState("");
    // micLevel and speakerLevel are now from hook
    const [localStream, setLocalStream] = useState(null);
    const sipRef = useRef(null);
    const remoteAudioRef = useRef(null);
    // mic refs removed
    // speaker refs removed

    const [incomingCall, setIncomingCall] = useState(null);
    const [incomingCallerInfo, setIncomingCallerInfo] = useState({});
    const [isOutbound, setIsOutbound] = useState(false);

    const [showKeypad, setShowKeypad] = useState(false);
    const [showAudioSettings, setShowAudioSettings] = useState(false);
    const [inputDevices, setInputDevices] = useState([]);
    const [selectedInput, setSelectedInput] = useState("");
    const [hasEarlyMedia, setHasEarlyMedia] = useState(false);

    const [dtmfDigits, setDtmfDigits] = useState("");

    const [selectedCallerId, setSelectedCallerId] = useState("");

    const [showCredentials, setShowCredentials] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [copiedField, setCopiedField] = useState("");
    const credentials = config?.credentials || {};

    const toggleCredentials = (e) => {
        e.stopPropagation();
        setShowCredentials((prev) => !prev);
    };

    const handleCopyCredential = async (field, value) => {
        if (!value) return;
        try {
            await navigator.clipboard.writeText(String(value));
            setCopiedField(field);
            setTimeout(() => setCopiedField((prev) => (prev === field ? "" : prev)), 1500);
        } catch (err) {
            console.error("Failed to copy credential", err);
        }
    };

    const [, updateSipStatus] = useAxios({ ...__updateSipSetting });
    const [sipStatusResponse, checkSipStatus] = useAxios({ ...__getSipSetting });

    // Audio Preview for Settings (Mic Testing)
    useEffect(() => {
        let activeStream = null;

        const startPreview = async () => {
            if (showAudioSettings && !inCall) {
                try {
                    const constraints = {
                        audio: selectedInput ? { deviceId: { exact: selectedInput } } : true
                    };
                    const stream = await navigator.mediaDevices.getUserMedia(constraints);
                    activeStream = stream;
                    setLocalStream(stream);
                } catch (err) {
                    console.error("Failed to get preview stream", err);
                }
            } else if (!showAudioSettings && !inCall && localStream) {
                // If closing settings and not in call, stop stream
                // Check if it's the preview stream? hard to track strict equality if state updates. 
                // But generally safe to stop if not in call.
                // Actually, let's rely on cleanup.
            }
        };

        startPreview();

        return () => {
            if (activeStream) {
                activeStream.getTracks().forEach(track => track.stop());
                // Only clear localStream if we are not in a call (sip.js managing it)
                if (!inCall) {
                    setLocalStream(null);
                }
            }
        };
    }, [showAudioSettings, selectedInput, inCall]);

    // Check initial SIP status from API
    useEffect(() => {
        checkSipStatus();
    }, []);

    // Handle SIP Status Response
    useEffect(() => {
        if (sipStatusResponse && sipStatusResponse.status) {
            if (sipStatusResponse.data && sipStatusResponse.data.online) {
                // Determine if we should be registered
                if (!isRegistered) {
                    setIsRegistered(true);
                }
            }
        }
    }, [sipStatusResponse]);


    useEffect(() => {
        if (callerIds.length > 0 && !selectedCallerId) {
            setSelectedCallerId(callerIds[0].value);
        }
    }, [callerIds, selectedCallerId]);

    const testSpeakerAudio = useRef(null);
    const testAudioCtxRef = useRef(null);
    const [isPlayingTest, setIsPlayingTest] = useState(false);

    // Cleanup test context on unmount
    useEffect(() => {
        return () => {
            if (testAudioCtxRef.current) {
                testAudioCtxRef.current.close().catch(() => { });
            }
        };
    }, []);

    const handleTestSpeaker = async () => {
        if (!testSpeakerAudio.current) return;

        try {
            if (isPlayingTest) {
                if (testAudioCtxRef.current) {
                    testAudioCtxRef.current.close().catch(() => { });
                    testAudioCtxRef.current = null;
                }
                testSpeakerAudio.current.srcObject = null;
                setIsPlayingTest(false);
            } else {
                if (typeof testSpeakerAudio.current.setSinkId === 'function' && selectedOutput) {
                    await testSpeakerAudio.current.setSinkId(selectedOutput);
                }

                const ctx = new (window.AudioContext || window.webkitAudioContext)();
                testAudioCtxRef.current = ctx;

                const oscillator = ctx.createOscillator();
                const gainNode = ctx.createGain();
                const dest = ctx.createMediaStreamDestination();

                oscillator.type = 'sine';
                oscillator.frequency.setValueAtTime(440, ctx.currentTime); // A4 Beep
                gainNode.gain.setValueAtTime(0.5, ctx.currentTime);

                oscillator.connect(gainNode);
                gainNode.connect(dest);

                testSpeakerAudio.current.srcObject = dest.stream;
                await testSpeakerAudio.current.play();
                oscillator.start();

                setIsPlayingTest(true);

                // Stop after 1 second
                setTimeout(() => {
                    if (testAudioCtxRef.current === ctx) {
                        ctx.close().catch(() => { });
                        testAudioCtxRef.current = null;
                        if (testSpeakerAudio.current) testSpeakerAudio.current.srcObject = null;
                        setIsPlayingTest(false);
                    }
                }, 1000);
            }
        } catch (e) {
            console.error("Speaker Test Failed", e);
            setIsPlayingTest(false);
        }
    };

    // Auto-register on mount if enabled and config ready
    useEffect(() => {
        if (config && isRegistered && !sipRef.current && !isRegistering) {
            handleRegister();
        }
    }, [config, isRegistered]);

    useEffect(() => {
        if (incomingCall) {
            setIsExpanded(true);
        }
    }, [incomingCall]);

    // Keypad digits
    const keypadDigits = [
        ["1", "2", "3"],
        ["4", "5", "6"],
        ["7", "8", "9"],
        ["*", "0", "#"],
    ];

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (sipRef.current) sipRef.current.stop();
            if (callTimerRef.current) clearInterval(callTimerRef.current);
        };
    }, []);

    const getDevices = useCallback(async () => {
        try {
            const devices = await navigator.mediaDevices.enumerateDevices();
            const outputs = devices.filter((d) => d.kind === "audiooutput");
            const inputs = devices.filter((d) => d.kind === "audioinput");

            setOutputDevices(outputs);
            setInputDevices(inputs);

            if (outputs.length > 0) {
                setSelectedOutput((prev) => (prev ? prev : outputs[0].deviceId));
            }
            if (inputs.length > 0) {
                setSelectedInput((prev) => (prev ? prev : inputs[0].deviceId));
            }
        } catch (err) {
            console.warn("Error fetching devices", err);
        }
    }, []);

    // Initial Permission Request & Device Query
    useEffect(() => {
        const initAudio = async () => {
            try {
                // Force permission request on mount
                const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
                stream.getTracks().forEach((t) => t.stop());
            } catch (e) {
                console.warn("Initial mic permission denied", e);
            }
            getDevices();
        };

        initAudio();

        navigator.mediaDevices.addEventListener("devicechange", getDevices);
        return () => {
            navigator.mediaDevices.removeEventListener("devicechange", getDevices);
        };
    }, [getDevices]);

    // Re-check when localStream changes
    useEffect(() => {
        if (localStream) getDevices();
    }, [localStream, getDevices]);

    const { micLevel, speakerLevel, speakerAudioCtxRef } = useAudioVisualizer(
        inCall || showAudioSettings,
        localStream,
        remoteAudioRef
    );

    // Format call duration as mm:ss
    const formatDuration = (secs) => {
        const m = Math.floor(secs / 60)
            .toString()
            .padStart(2, "0");
        const s = (secs % 60).toString().padStart(2, "0");
        return `${m}:${s}`;
    };

    useRingtone(incomingCall);
    useRingback(isCalling && isOutbound && callStatus === "Ringing..." && !hasEarlyMedia);


    const handleRegister = async () => {
        if (isRegistering) return; // Prevent double registration
        if (sipRef.current && sipRef.current.userAgent && sipRef.current.userAgent.isConnected()) return; // Already connected logic backup

        setIsRegistering(true);
        sipRef.current = new SIPClient(
            {
                ...config,
                onIncomingCall: (invitation) => {
                    // Extract caller info from invitation.request
                    const callerId =
                        invitation.request.from &&
                        invitation.request.from.uri &&
                        invitation.request.from.uri.user;
                    const displayName =
                        invitation.request.from && invitation.request.from.displayName;

                    // Try to get custom header for original destination
                    let destination = invitation.request.getHeader
                        ? invitation.request.getHeader("X-Destination-Number")
                        : null;

                    if (!destination) {
                        destination =
                            invitation.request.to &&
                            invitation.request.to.uri &&
                            invitation.request.to.uri.user;
                    }

                    // Handle remote cancelation while ringing
                    invitation.stateChange.addListener((state) => {
                        if (state === "Terminated" || state === "Canceled") {
                            setIncomingCall(null);
                            setIncomingCallerInfo({});
                        }
                    });

                    setIncomingCall(invitation);
                    setIncomingCallerInfo({
                        callerId: callerId || "Unknown",
                        displayName: displayName || callerId || "Unknown",
                        destination: destination || "Me",
                    });

                    // Auto-expand on incoming call logic if desired
                    // setIsExpanded(true); 
                },
                onConnectionStateChange: (state) => {
                    if (state === "disconnected") {
                        console.warn("SIP Transport Disconnected. Attempting to re-register...");

                        if (inCall) {
                            setCallStatus("Connection Lost");
                            // Give UI a moment to show "Connection Lost" before resetting
                            setTimeout(() => {
                                setInCall(false);
                                setCallDuration(0);
                                setCallStatus("");
                            }, 2000);
                        }

                        // Attempt to re-register automatically
                        handleRegister();
                    } else if (state === "connected") {
                        setIsRegistered(true);
                    }
                }
            },
            remoteAudioRef
        );

        // Listen for call progress (Ringing, etc.)
        sipRef.current.onCallProgress = (response) => {
            if (response.message.statusCode === 180) {
                setCallStatus("Ringing...");
                setHasEarlyMedia(false);
            } else if (response.message.statusCode === 183) {
                setCallStatus("Ringing...");
                // Check if response has SDP body (early media)
                if (response.message.body) {
                    setHasEarlyMedia(true);
                } else {
                    setHasEarlyMedia(false);
                }
            } else if (response.message.statusCode < 200) {
                setCallStatus("Connecting...");
            }
        };
        // Listen for session state changes
        sipRef.current.onSessionStateChange = (state, session) => {
            const stateStr = typeof state === "string"
                ? state
                : state?.toString?.() || state?.name || String(state);

            if (stateStr === "Established") {
                setCallStatus("Connected");
                setIsCalling(false);
                setInCall(true);
                setDtmfDigits("");

                // Reset duration to 0 and start timer if not already running
                // Clear any existing timer just in case
                if (callTimerRef.current) clearInterval(callTimerRef.current);
                setCallDuration(0);

                const timer = setInterval(() => setCallDuration((d) => d + 1), 1000);
                callTimerRef.current = timer;
            } else if (stateStr === "Terminated") {
                setCallStatus("Ended");
                // Check if it was a server error (500)
                // SIP.js session doesn't easily expose the last response in 'stateChange' param directly, 
                // but we can infer or rely on logs. 
                // If user wants re-register on "weird" failures, we can check registration status.

                setIsCalling(false);
                setShowKeypad(false);
                setShowAudioSettings(false);
                setDtmfDigits("");
                setIsMuted(false); // Reset mute state

                if (callTimerRef.current) {
                    clearInterval(callTimerRef.current);
                    callTimerRef.current = null;
                }

                setTimeout(() => {
                    setInCall(false);
                    setCallDuration(0);
                    setCallStatus("Ready");
                    setDialNumber("");
                    setIsOutbound(false);
                }, 2000);
            }

            // Update session ID if available
            if (session && session.request) {
                setSessionId(session.request.callId || session.request.callID || "");
            }
        };
        // Listen for local stream
        sipRef.current.onLocalStream = (stream) => setLocalStream(stream);
        try {
            await sipRef.current.start();
            setIsRegistered(true);
            updateSipStatus({ data: { online: true } });
        } catch (err) {
            alert("Registration failed");
            setIsRegistered(false);
        }
        setIsRegistering(false);
    };

    const handleUnregister = async () => {
        setIsRegistering(true);
        try {
            await sipRef.current.stop();
        } catch { }
        setIsRegistered(false);
        setIsRegistering(false);
        setIsCalling(false);
        setIsOutbound(false);
        setDialNumber("");
        setInCall(false);
        setCallStatus("");
        setCallDuration(0);
        setSessionId("");
        if (callTimerRef.current) {
            clearInterval(callTimerRef.current);
            callTimerRef.current = null;
        }
        setIsMuted(false);
        setLocalStream(null);
        setShowKeypad(false);
        setDtmfDigits("");
        setHasEarlyMedia(false);
        updateSipStatus({ data: { online: false } });
    };

    const handleCall = () => {
        if (!dialNumber) return;
        setCallStatus("Dialing...");
        setInCall(true);
        setIsCalling(true);
        setIsOutbound(true);
        setCallDuration(0);
        setSessionId("");
        setDtmfDigits("");
        setHasEarlyMedia(false);

        const domain = config.uri.split("@")[1];
        const targetUri = `sip:${dialNumber.replace(/\s+/g, "")}@${domain}`;

        const extraHeaders = [];
        if (selectedCallerId) {
            extraHeaders.push(`X-CALL-FROM: ${selectedCallerId}`);
        }

        // Initialize Speaker Context on User Gesture to prevent suspension
        try {
            const ctx = new (window.AudioContext || window.webkitAudioContext)();
            ctx.resume().then(() => {
                speakerAudioCtxRef.current = ctx;
            });
        } catch (e) { console.warn("AudioCtx init failed", e); }

        sipRef.current.call(targetUri, extraHeaders);
    };

    const handleHangup = () => {
        sipRef.current.hangup();
        setDtmfDigits("");
    };

    const handleKeypadClick = (digit) => {
        if (inCall && !isCalling && sipRef.current) {
            sipRef.current.sendDTMF(digit);
            setDtmfDigits(prev => prev + digit);
        } else {
            setDialNumber(dialNumber + digit);
        }
    };

    const handleBackspace = () => {
        setDialNumber(dialNumber.slice(0, -1));
    };

    // const handleClear = () => {
    //     setDialNumber("");
    // };

    const handleMute = () => {
        const newMute = !isMuted;
        setIsMuted(newMute);
        if (sipRef.current) sipRef.current.setMuted(newMute);
    };

    const handleSpeakerChange = async (e) => {
        const deviceId = e.target.value;
        setSelectedOutput(deviceId);
        if (sipRef.current) await sipRef.current.setOutputDevice(deviceId);
    };

    const handleMicChange = async (e) => {
        const deviceId = e.target.value;
        setSelectedInput(deviceId);
        if (sipRef.current) await sipRef.current.setInputDevice(deviceId);
    };

    // Accept incoming SIP call
    const handleAcceptIncoming = async () => {
        if (sipRef.current && incomingCall) {
            setIncomingCall(null);
            setInCall(true);
            setIsOutbound(false);
            setCallStatus("Connecting...");
            setDialNumber(incomingCallerInfo.callerId || "");

            // Initialize Speaker Context on User Gesture
            try {
                const ctx = new (window.AudioContext || window.webkitAudioContext)();
                ctx.resume().then(() => {
                    speakerAudioCtxRef.current = ctx;
                });
            } catch (e) { console.warn("AudioCtx init failed", e); }

            try {
                await sipRef.current.acceptIncomingCall();
            } catch (err) {
                console.error("Accept failed", err);
                setInCall(false);
                setCallStatus("Failed");
            }

            setIncomingCallerInfo({});
        }
    };

    const handleRejectIncoming = async () => {
        if (sipRef.current && incomingCall) {
            await sipRef.current.rejectIncomingCall();
            setIncomingCall(null);
            setIncomingCallerInfo({});
        }
    };

    const toggleRegister = (e) => {
        e.stopPropagation();
        if (isRegistered) {
            handleUnregister();
        } else {
            handleRegister();
        }
    };

    const toggleExpand = () => {
        setIsExpanded(!isExpanded);
    };

    return (
        <div className={`sip-widget ${isExpanded ? 'expanded' : 'minimized'}`} style={style}>
            {/* Hidden Audio Element */}
            <audio ref={remoteAudioRef} autoPlay style={{ position: 'absolute', width: 0, height: 0, opacity: 0 }} />
            <audio ref={testSpeakerAudio} style={{ position: 'absolute', width: 0, height: 0, opacity: 0 }} />

            {/* Minimized Header Bar */}
            {!isExpanded && (
                <div className="widget-header" onClick={toggleExpand}>
                    <div className="widget-status">
                        <div className={`status-icon-circle ${isRegistered ? 'online' : 'offline'}`}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                            </svg>
                        </div>
                        <div className="status-info-col">
                            <span className="status-label">
                                {inCall ? (callStatus || "In Call") : (isRegistered ? "Ready" : "Offline")}
                            </span>
                            {inCall && <span className="status-timer">{formatDuration(callDuration)}</span>}
                        </div>
                    </div>
                    <div className="widget-controls">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="6 9 12 15 18 9"></polyline>
                        </svg>
                    </div>
                </div>
            )}

            {/* Expanded Content */}
            <div className={`widget-content ${isExpanded ? 'visible' : ''}`}>
                <div className="widget-top-bar" onClick={toggleExpand}>
                    <span className="widget-title">Phone</span>
                    <div className="top-bar-actions">
                        <button
                            className="minimize-btn"
                            onClick={toggleCredentials}
                            title="SIP Credentials"
                        >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                                <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                            </svg>
                        </button>
                        <button className="minimize-btn">▲</button>
                    </div>
                </div>

                <div className="glass-panel dialer-panel widget-panel">

                    {showCredentials && (
                        <div className="credentials-overlay" onClick={(e) => e.stopPropagation()}>
                            <div className="overlay-header">
                                <span className="widget-title">SIP Credentials</span>
                                <button className="close-btn" onClick={() => setShowCredentials(false)}>✖</button>
                            </div>
                            {[
                                { key: "host", label: "Host", value: credentials.host },
                                { key: "port", label: "Port", value: credentials.port },
                                { key: "user", label: "User", value: credentials.user },
                                { key: "password", label: "Password", value: credentials.password, secret: true },
                            ].map(({ key, label, value, secret }) => (
                                <div className="cred-row" key={key}>
                                    <span className="cred-label">{label}</span>
                                    <div className="cred-value-group">
                                        <span className="cred-value">
                                            {secret && !showPassword ? "••••••••" : (value || "—")}
                                        </span>
                                        {secret && (
                                            <button
                                                className="cred-icon-btn"
                                                onClick={() => setShowPassword((prev) => !prev)}
                                                title={showPassword ? "Hide" : "Show"}
                                            >
                                                {showPassword ? (
                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                        <path d="M17.94 17.94A10.94 10.94 0 0 1 12 20c-7 0-11-8-11-8a20.29 20.29 0 0 1 5.06-5.94M9.9 4.24A10.94 10.94 0 0 1 12 4c7 0 11 8 11 8a20.29 20.29 0 0 1-3.22 4.31M14.12 14.12a3 3 0 1 1-4.24-4.24"></path>
                                                        <line x1="1" y1="1" x2="23" y2="23"></line>
                                                    </svg>
                                                ) : (
                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                                                        <circle cx="12" cy="12" r="3"></circle>
                                                    </svg>
                                                )}
                                            </button>
                                        )}
                                        <button
                                            className={`cred-icon-btn ${copiedField === key ? "copied" : ""}`}
                                            onClick={() => handleCopyCredential(key, value)}
                                            title="Copy"
                                            disabled={!value}
                                        >
                                            {copiedField === key ? (
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                                    <polyline points="20 6 9 17 4 12"></polyline>
                                                </svg>
                                            ) : (
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                                                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                                                </svg>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Registration Toggle */}
                    <div className="toggle-container">
                        <span className="toggle-label">
                            {isRegistering
                                ? "Registering..."
                                : isRegistered
                                    ? "Online"
                                    : "Offline"}
                        </span>
                        <div
                            className={`toggle-switch ${isRegistered ? "active" : ""}`}
                            onClick={!isRegistering ? toggleRegister : undefined}
                            title={isRegistered ? "Go Offline" : "Go Online"}
                        >
                            <div className="toggle-knob"></div>
                        </div>
                    </div>

                    {incomingCall ? (
                        <div className="incoming-call-panel" style={{ textAlign: 'center', padding: '20px 0', animation: 'fadeIn 0.3s' }}>
                            <div className="icon-container pulsate" style={{ margin: '0 auto 20px', width: '64px', height: '64px', borderRadius: '50%', background: 'var(--sip-accent-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '32px' }}>
                                <span className="phone-icon">📞</span>
                            </div>
                            <div className="modal-title" style={{ marginBottom: '20px', color: 'white', fontWeight: 600 }}>Incoming Call</div>

                            <div className="call-info-group">
                                <div className="info-row">
                                    <span className="info-label">From</span>
                                    <span className="info-value">
                                        {incomingCallerInfo.callerId || "Unknown"}
                                    </span>
                                </div>
                                <div className="info-row">
                                    <span className="info-label">To</span>
                                    <span className="info-value">
                                        {incomingCallerInfo.destination || "Me"}
                                    </span>
                                </div>
                            </div>

                            <div className="control-row" style={{ marginTop: '30px' }}>
                                <button
                                    onClick={handleRejectIncoming}
                                    className="btn-reject"
                                    title="Reject"
                                >
                                    ✖
                                </button>
                                <div style={{ width: '20px' }}></div>
                                <button
                                    onClick={handleAcceptIncoming}
                                    className="btn-accept"
                                    title="Accept"
                                >
                                    📞
                                </button>
                            </div>
                        </div>
                    ) : !inCall ? (
                        <>
                            {/* Caller ID Selection */}
                            <div className="caller-id-wrapper">
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <label className="input-label">Caller ID</label>
                                    <button
                                        className="minimize-btn"
                                        onClick={(e) => { e.stopPropagation(); setShowAudioSettings(!showAudioSettings); }}
                                        title="Audio Settings"
                                        style={{ fontSize: '10px', padding: '2px 5px', marginBottom: '4px' }}
                                    >
                                        ⚙️ Audio
                                    </button>
                                </div>
                                <Autocomplete
                                    options={callerIdOptions}
                                    getOptionLabel={(option) => option.label || ""}
                                    value={callerIdOptions.find(opt => opt.value === selectedCallerId) || null}
                                    onChange={(event, newValue) => {
                                        setSelectedCallerId(newValue ? newValue.value : "");
                                    }}
                                    disableClearable
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            variant="outlined"
                                            size="small"
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    backgroundColor: "rgba(255, 255, 255, 0.05)",
                                                    color: "white",
                                                    "& fieldset": {
                                                        borderColor: "rgba(255, 255, 255, 0.1)",
                                                    },
                                                    "&:hover fieldset": {
                                                        borderColor: "rgba(255, 255, 255, 0.3)",
                                                    },
                                                    "&.Mui-focused fieldset": {
                                                        borderColor: "#6366f1",
                                                    },
                                                },
                                                "& .MuiInputBase-input": {
                                                    fontSize: "13px",
                                                    color: "white",
                                                },
                                                "& .MuiSvgIcon-root": {
                                                    color: "#94a3b8",
                                                },
                                            }}
                                        />
                                    )}
                                    componentsProps={{
                                        paper: {
                                            sx: {
                                                backgroundColor: "#1e293b",
                                                color: "white",
                                                "& .MuiMenuItem-root": {
                                                    "&.Mui-selected": {
                                                        backgroundColor: "rgba(99, 102, 241, 0.2)",
                                                    },
                                                    "&:hover": {
                                                        backgroundColor: "rgba(255, 255, 255, 0.1)",
                                                    },
                                                },
                                            },
                                        },
                                    }}
                                />
                            </div>

                            {showAudioSettings && (
                                <>
                                    <div
                                        className="settings-backdrop"
                                        onClick={() => setShowAudioSettings(false)}
                                    ></div>
                                    {renderAudioSettings()}
                                </>
                            )}

                            <div className="display-area">
                                <input
                                    type="text"
                                    value={dialNumber}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        if (/^[0-9+]*$/.test(val)) {
                                            setDialNumber(val);
                                        }
                                    }}
                                    placeholder="Enter number..."
                                    className="dial-input"
                                    maxLength={20}
                                />
                                <div className="status-text">{callStatus || (isRegistered ? "Ready" : "Register to call")}</div>
                            </div>

                            <div className="keypad-grid">
                                {keypadDigits.flat().map((digit) => (
                                    <button
                                        key={digit}
                                        onClick={() => handleKeypadClick(digit)}
                                        className="keypad-btn"
                                        disabled={!isRegistered}
                                    >
                                        {digit}
                                        <span className="sub-text">
                                            {digit === "1" ? "" : digit === "2" ? "ABC" : digit === "3" ? "DEF"
                                                : digit === "4" ? "GHI" : digit === "5" ? "JKL" : digit === "6" ? "MNO"
                                                    : digit === "7" ? "PQRS" : digit === "8" ? "TUV" : digit === "9" ? "WXYZ"
                                                        : digit === "0" ? "+" : ""}
                                        </span>
                                    </button>
                                ))}
                            </div>

                            <div className="control-row">
                                <button
                                    onClick={handleBackspace}
                                    className="btn-action btn-backspace"
                                    disabled={!isRegistered || !dialNumber}
                                >
                                    ⌫
                                </button>
                                <button
                                    onClick={handleCall}
                                    className="btn-action btn-call"
                                    disabled={!isRegistered || !dialNumber || isCalling}
                                >
                                    📞
                                </button>
                            </div>
                        </>
                    ) : (
                        <>
                            {/* In Call View */}
                            {showKeypad && (
                                <div className="keypad-overlay">
                                    <div className="overlay-header">
                                        <button className="close-btn" onClick={() => setShowKeypad(false)}>✖</button>
                                    </div>
                                    <div className="display-area">
                                        <div className="dial-input large">
                                            {dtmfDigits}
                                        </div>
                                        <div className="status-text">
                                            {dtmfDigits ? "Sending tones..." : "Type to send tones"}
                                        </div>
                                    </div>
                                    <div className="keypad-grid">
                                        {keypadDigits.flat().map((digit) => (
                                            <button
                                                key={digit}
                                                onClick={() => handleKeypadClick(digit)}
                                                className="keypad-btn"
                                            >
                                                {digit}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {showAudioSettings && (
                                <div
                                    className="settings-backdrop"
                                    onClick={() => setShowAudioSettings(false)}
                                ></div>
                            )}

                            <div className="display-area in-call">
                                <div className="status-value connected">
                                    {callStatus || "Connected"}
                                </div>
                                {isOutbound && selectedCallerId && (
                                    <div className="status-detail">
                                        From: {selectedCallerId}
                                    </div>
                                )}
                                <div className="dial-input large">
                                    {dialNumber}
                                </div>
                                <div className="status-text timer">
                                    {formatDuration(callDuration)}
                                </div>
                            </div>

                            <div className={`incall-grid ${isCalling ? 'disabled' : ''}`}>
                                <div
                                    className={`incall-btn ${isMuted ? "active" : ""}`}
                                    onClick={handleMute}
                                >
                                    <span className="incall-btn-icon">{isMuted ? "🔇" : "🎤"}</span>
                                    <span className="incall-btn-label">
                                        {isMuted ? "Unmute" : "Mute"}
                                    </span>
                                </div>
                                <div
                                    className={`incall-btn ${showKeypad ? "active" : ""}`}
                                    onClick={() => {
                                        setShowKeypad(true);
                                        setShowAudioSettings(false);
                                    }}
                                >
                                    <span className="incall-btn-icon">#</span>
                                    <span className="incall-btn-label">Keypad</span>
                                </div>
                                <div
                                    className={`incall-btn ${showAudioSettings ? "active" : ""}`}
                                    onClick={() => {
                                        setShowAudioSettings(!showAudioSettings);
                                        setShowKeypad(false);
                                    }}
                                >
                                    <span className="incall-btn-icon">🔊</span>
                                    <span className="incall-btn-label">Audio</span>
                                </div>
                            </div>

                            {showAudioSettings && (
                                <>
                                    <div
                                        className="settings-backdrop"
                                        onClick={() => setShowAudioSettings(false)}
                                    ></div>
                                    {renderAudioSettings()}
                                </>
                            )}

                            <div className="control-row hangup-row">
                                <button
                                    onClick={handleHangup}
                                    className="btn-action btn-hangup"
                                >
                                    📞
                                </button>
                            </div>

                            <div className="audio-meter">
                                <div className="meter-label">
                                    <span>Mic</span>
                                </div>
                                <div className="meter-track">
                                    <div className="meter-fill" style={{ width: `${Math.min(micLevel * 200, 100)}%` }}></div>
                                </div>

                                <div className="meter-label">
                                    <span>Speaker</span>
                                </div>
                                <div className="meter-track">
                                    <div className="meter-fill" style={{ width: `${Math.min(speakerLevel * 200, 100)}%` }}></div>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>


        </div>
    );
}

export default SipPhoneWidget;
